import React, {useEffect, useState} from 'react';
import {
  Modal,
  Table,
  Tag,
  Empty,
  Typography,
  InputNumber,
  Input,
  Button,
  Space,
  Select,
  message,
  Alert,
  Tooltip,
} from 'antd';
import {useDispatch} from 'react-redux';
import dayjs from 'dayjs';
import {
  getUserCreditHistory,
  updateUserCredit,
} from '../../../Store/Actions/registrationHistory';
import {formatCurrencyByLocalChapter} from '../../../Util/commonUtility';
import {ProtectedResource} from '../ProtectedResource';

const {Text} = Typography;

const CREDIT_SOURCE_LABELS = {
  refund_to_credit: 'Refunded to credit',
  used_at_checkout: 'Used at checkout',
  cancellation_restore: 'Returned on cancellation',
  payment_failed_restore: 'Returned after failed payment',
  duplicate_cleanup_restore: 'Returned on duplicate cleanup',
  chapter_currency_conversion: 'Chapter currency conversion',
  manual_adjustment: 'Manual adjustment',
  registration: 'Registration',
};

/**
 * Per-member credit history modal. Fetches credit movements from
 * GET /users/:id/credit-history — registration-driven rows are reconstructed, manual admin
 * adjustments come from the credit_adjustments ledger — and shows a running balance.
 *
 * Admins can also apply a manual adjustment (signed amount + optional note); on success the
 * history and current balance refresh, and `onUpdated` lets the parent refresh its listing.
 */
const CreditHistoryModal = ({
  open,
  onClose,
  userId,
  memberName,
  localChapterData,
  onUpdated,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [unrecordedDifference, setUnrecordedDifference] = useState(0);
  const [adjustAmount, setAdjustAmount] = useState(null);
  const [adjustDirection, setAdjustDirection] = useState('add');
  const [adjustNote, setAdjustNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchHistory = () => {
    setLoading(true);
    dispatch(getUserCreditHistory(userId)).then((data) => {
      setRows(data?.creditHistory || []);
      setCurrentBalance(data?.current_balance || 0);
      setUnrecordedDifference(Number(data?.unrecorded_difference || 0));
      setLoading(false);
    });
  };

  useEffect(() => {
    if (open && userId) {
      fetchHistory();
      setAdjustAmount(null);
      setAdjustDirection('add');
      setAdjustNote('');
    }
  }, [open, userId]);

  const handleSaveAdjustment = () => {
    const amount = Math.round(Number(adjustAmount) * 100) / 100;
    if (
      adjustAmount === null ||
      adjustAmount === undefined ||
      Number.isNaN(amount) ||
      amount < 0
    ) {
      message.warning('Enter a valid amount (0 or greater).');
      return;
    }
    // Add requires a positive amount; a 0 add does nothing.
    if (adjustDirection === 'add' && amount <= 0) {
      message.warning('Enter an amount greater than 0 to add credit.');
      return;
    }
    // Remove with 0 empties the member's entire remaining credit balance.
    let signedAmount;
    if (adjustDirection === 'remove') {
      const balance = Number(currentBalance) || 0;
      if (balance <= 0) {
        message.warning('This member has no credit to remove.');
        return;
      }
      if (amount === 0) {
        signedAmount = -Math.abs(balance);
      } else {
        // Cannot remove more than the member currently has — would make the balance negative.
        if (amount > balance) {
          message.warning(
            `Cannot remove more than the current balance (${formatCurrencyByLocalChapter(
              balance,
              localChapterData,
              2,
              2,
            )}).`,
          );
          return;
        }
        signedAmount = -Math.abs(amount);
      }
    } else {
      signedAmount = Math.abs(amount);
    }
    setSaving(true);
    dispatch(
      updateUserCredit(userId, {
        amount: signedAmount,
        note: adjustNote?.trim() || undefined,
      }),
    ).then((data) => {
      setSaving(false);
      // Surface the API error (e.g. adjustment would make the balance negative).
      if (data?.error) {
        message.error(data.error);
        return;
      }
      if (data) {
        message.success('Member credit updated.');
        setAdjustAmount(null);
        setAdjustDirection('add');
        setAdjustNote('');
        fetchHistory();
        onUpdated && onUpdated(userId, data?.current_balance);
      }
    });
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (value) =>
        value ? dayjs(value).format('YYYY-MM-DD hh:mm A') : 'N/A',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      render: (value) => (
        <Tag color={value === 'earned' ? 'green' : 'volcano'}>
          {value === 'earned' ? 'Earned' : 'Used'}
        </Tag>
      ),
    },
    {
      title: 'Details',
      dataIndex: 'event_name',
      key: 'event_name',
      render: (value, record) => (
        <div>
          <div>
            {CREDIT_SOURCE_LABELS[record.source] || 'Credit change'}
            {record.is_reconstructed && (
              <Tooltip title='Recorded before the credit ledger. Balances are estimated from registration data.'>
                <Tag style={{marginLeft: 6}}>Estimated</Tag>
              </Tooltip>
            )}
          </div>
          {value && record.source !== 'manual_adjustment' && (
            <Text type='secondary' style={{fontSize: 12, display: 'block'}}>
              {value}
            </Text>
          )}
          {(record.admin_name || record.note) && (
            <Text type='secondary' style={{fontSize: 12, display: 'block'}}>
              {record.admin_name ? `by ${record.admin_name}` : ''}
              {record.admin_name && record.note ? ' · ' : ''}
              {record.note || ''}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Reg. ID',
      dataIndex: 'event_registration_id',
      key: 'event_registration_id',
      width: 80,
      render: (value) => value ?? 'N/A',
    },
    {
      title: 'Balance Before',
      dataIndex: 'balance_before',
      key: 'balance_before',
      width: 120,
      align: 'right',
      render: (value) =>
        value === null || value === undefined
          ? 'N/A'
          : formatCurrencyByLocalChapter(value, localChapterData),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      align: 'right',
      render: (value, record) => (
        <Text type={record.type === 'earned' ? 'success' : 'danger'}>
          {record.type === 'earned' ? '+' : '-'}
          {formatCurrencyByLocalChapter(value, localChapterData, 2, 2)}
        </Text>
      ),
    },
    {
      title: 'Balance After',
      dataIndex: 'balance_after',
      key: 'balance_after',
      width: 120,
      align: 'right',
      render: (value, record) => {
        const balance = value ?? record.resulting_balance;
        return balance === null || balance === undefined ? (
          'N/A'
        ) : (
          <Text strong>
            {formatCurrencyByLocalChapter(balance, localChapterData, 2, 2)}
          </Text>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1100}
      title={`Credit History${memberName ? ` — ${memberName}` : ''}`}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          marginBottom: 20,
          background: '#f5f7fa',
          borderRadius: 8,
        }}>
        <Text strong style={{fontSize: 15}}>
          Current Balance
        </Text>
        <Text strong style={{fontSize: 18}}>
          {formatCurrencyByLocalChapter(currentBalance, localChapterData, 2, 2)}
        </Text>
      </div>
      {unrecordedDifference !== 0 && (
        <Alert
          type='warning'
          showIcon
          style={{marginBottom: 20}}
          message={`The current balance differs from the last recorded entry by ${formatCurrencyByLocalChapter(
            unrecordedDifference,
            localChapterData,
          )}. This change was made outside the credit history.`}
        />
      )}
      <ProtectedResource resource='member_attendees' permission='update'>
        <div
          style={{
            padding: 16,
            marginBottom: 20,
            border: '1px solid #f0f0f0',
            borderRadius: 8,
          }}>
          <Text strong style={{display: 'block', marginBottom: 12}}>
            Adjust Credit
          </Text>
          <Space size='middle' align='start' wrap>
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <InputNumber
                style={{width: 200}}
                value={adjustAmount}
                onChange={setAdjustAmount}
                placeholder='Amount'
                min={0}
                step={0.01}
                precision={2}
                stringMode
                addonBefore={
                  <Select
                    value={adjustDirection}
                    onChange={setAdjustDirection}
                    style={{width: 90}}
                    options={[
                      {value: 'add', label: 'Add'},
                      {value: 'remove', label: 'Remove'},
                    ]}
                  />
                }
              />
              <Text type='secondary' style={{fontSize: 12, marginTop: 4}}>
                {adjustDirection === 'add'
                  ? 'Increase member credit'
                  : 'Decrease member credit — enter 0 to empty the entire credit'}
              </Text>
            </div>
            <Input
              style={{width: 380}}
              value={adjustNote}
              onChange={(e) => setAdjustNote(e.target.value)}
              placeholder='Note (optional)'
              maxLength={255}
            />
            <Button
              type='primary'
              loading={saving}
              onClick={handleSaveAdjustment}>
              Update Credit
            </Button>
          </Space>
        </div>
      </ProtectedResource>
      <Table
        rowKey='id'
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        size='small'
        tableLayout='fixed'
        scroll={{y: 360}}
        locale={{
          emptyText: (
            <Empty description='No credit activity for this member.' />
          ),
        }}
      />
    </Modal>
  );
};

export default CreditHistoryModal;
